 /**
  * Utilitários de cálculo para precificação de produtos
  */
 
 export type MarginStatus = 'healthy' | 'warning' | 'critical';
 
 /**
  * Calcula o preço sugerido baseado no custo e margem desejada
  * Fórmula: Custo / (1 - MargemDesejada/100)
  */
 export function calculateSuggestedPrice(cost: number, targetMarginPercent: number): number {
   if (targetMarginPercent >= 100) return cost * 100; // Limite superior
   if (targetMarginPercent <= 0) return cost;
   return cost / (1 - targetMarginPercent / 100);
 }
 
 /**
  * Calcula o CMV (Custo da Mercadoria Vendida) em percentual
  * Fórmula: (Custo / Preço) × 100
  */
 export function calculateCMVPercentage(cost: number, price: number): number {
   if (price <= 0) return 0;
   return (cost / price) * 100;
 }
 
 /**
  * Calcula a margem bruta (Preço - CMV)
  */
 export function calculateGrossMargin(price: number, cost: number): number {
   return price - cost;
 }
 
 /**
  * Calcula a margem real em percentual
  * Fórmula: ((Preço - Custo) / Preço) × 100
  */
 export function calculateRealMarginPercent(price: number, cost: number): number {
   if (price <= 0) return 0;
   return ((price - cost) / price) * 100;
 }
 
 /**
  * Calcula a margem de contribuição após despesas variáveis
  * Fórmula: MargemBruta - (Preço × TaxaVariável/100)
  */
 export function calculateContributionMargin(
   price: number,
   cost: number,
   variableExpensePercent: number
 ): number {
   const grossMargin = calculateGrossMargin(price, cost);
   const variableExpense = price * (variableExpensePercent / 100);
   return grossMargin - variableExpense;
 }
 
 /**
  * Calcula o lucro estimado após todos os custos
  * Fórmula: MargemContribuição - RateioFixoPorUnidade
  */
 export function calculateEstimatedProfit(
   price: number,
   cost: number,
   variableExpensePercent: number,
   fixedExpensePerUnit: number
 ): number {
   const contributionMargin = calculateContributionMargin(price, cost, variableExpensePercent);
   return contributionMargin - fixedExpensePerUnit;
 }
 
 /**
  * Determina o status da margem baseado na margem real vs desejada
  * - Saudável: MargemReal >= MargemDesejada
  * - Atenção: MargemReal entre 50-100% da desejada
  * - Crítica: MargemReal < 50% da desejada ou negativa
  */
 export function getMarginStatus(
   realMargin: number,
   targetMargin: number
 ): MarginStatus {
   if (realMargin < 0) return 'critical';
   if (realMargin >= targetMargin) return 'healthy';
   if (realMargin >= targetMargin * 0.5) return 'warning';
   return 'critical';
 }
 
 /**
  * Formata valor para moeda brasileira
  */
 export function formatCurrency(value: number): string {
   return new Intl.NumberFormat("pt-BR", {
     style: "currency",
     currency: "BRL",
   }).format(value);
 }
 
 /**
  * Formata valor para percentual
  */
 export function formatPercent(value: number, decimals: number = 1): string {
   return `${value.toFixed(decimals)}%`;
 }